// OrbitGizmo — 3 цветных круга вокруг target камеры (Fusion-style).
// Активен когда cameraTool === 'orbit'.
//
// Работа:
//   - hover на круг → выделяется (толще, ярче).
//   - pointerDown на круг → запоминаем угол → drag даёт angularDelta →
//     orbitAroundAxis(axis, angularDelta) (window API).
//
// Рисуется внутри Canvas, поэтому имеет доступ к camera через useThree.

import React, { useMemo, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import useCadStore from '../store/useCadStore';


// 3 оси (в которых нормаль круга = ось вращения).
// label — для tooltip; цвет — в стиле Red(X)/Green(Y)/Blue(Z).
const AXES = [
    { id: 'X', axis: [1, 0, 0], color: '#cc3344', hover: '#ff6677' },
    { id: 'Y', axis: [0, 1, 0], color: '#0c8',    hover: '#5d8' },
    { id: 'Z', axis: [0, 0, 1], color: '#3a78ff', hover: '#7aa8ff' },
];

const SEGMENTS = 64;


/** Возвращает массив точек круга радиуса R в плоскости с нормалью axis. */
function circlePoints(axisVec, radius) {
    const ax = new THREE.Vector3(axisVec[0], axisVec[1], axisVec[2]).normalize();
    // Базис в плоскости (u, v) перпендикулярный ax
    const tmp = Math.abs(ax.x) < 0.9
        ? new THREE.Vector3(1, 0, 0)
        : new THREE.Vector3(0, 1, 0);
    const u = new THREE.Vector3().crossVectors(tmp, ax).normalize();
    const v = new THREE.Vector3().crossVectors(ax, u).normalize();
    const pts = new Float32Array((SEGMENTS + 1) * 3);
    for (let i = 0; i <= SEGMENTS; i++) {
        const t = (i / SEGMENTS) * Math.PI * 2;
        const c = Math.cos(t), s = Math.sin(t);
        pts[i * 3]     = u.x * radius * c + v.x * radius * s;
        pts[i * 3 + 1] = u.y * radius * c + v.y * radius * s;
        pts[i * 3 + 2] = u.z * radius * c + v.z * radius * s;
    }
    return { pts, u, v };
}


const OrbitCircle = ({ axisDef, target, radius, hovered, setHovered, onDragAngle }) => {
    const { gl } = useThree();
    const dragRef = useRef(null);

    const { pts, u, v } = useMemo(
        () => circlePoints(axisDef.axis, radius),
        [axisDef.axis, radius]
    );

    const lineGeom = useMemo(() => {
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(pts, 3));
        return g;
    }, [pts]);

    // Тор для hit-test (с большим радиусом трубки, чтобы было удобно ловить)
    const torusGeom = useMemo(
        () => new THREE.TorusGeometry(radius, radius * 0.05, 8, SEGMENTS),
        [radius]
    );
    // Quaternion который ставит TorusGeometry (плоскость XY, нормаль +Z) в нужную ось.
    const torusQuat = useMemo(() => {
        const z = new THREE.Vector3(0, 0, 1);
        const target = new THREE.Vector3(...axisDef.axis).normalize();
        const q = new THREE.Quaternion().setFromUnitVectors(z, target);
        return q;
    }, [axisDef.axis]);

    // Угол точки относительно центра круга (target) в плоскости (u, v)
    const angleAt = (p) => {
        const x = (p.x - target[0]) * u.x + (p.y - target[1]) * u.y + (p.z - target[2]) * u.z;
        const y = (p.x - target[0]) * v.x + (p.y - target[1]) * v.y + (p.z - target[2]) * v.z;
        return Math.atan2(y, x);
    };

    const onDown = (e) => {
        e.stopPropagation();
        const p = e.point;
        if (!p) return;
        dragRef.current = { lastAngle: angleAt(p) };
        try { gl.domElement.setPointerCapture(e.pointerId); } catch (_) {}
    };
    const onMove = (e) => {
        if (!dragRef.current) return;
        const p = e.point;
        if (!p) return;
        const a = angleAt(p);
        let d = a - dragRef.current.lastAngle;
        // Нормализуем в (-π, π]
        if (d > Math.PI) d -= Math.PI * 2;
        if (d < -Math.PI) d += Math.PI * 2;
        dragRef.current.lastAngle = a;
        onDragAngle(d);
    };
    const onUp = (e) => {
        if (!dragRef.current) return;
        dragRef.current = null;
        try { gl.domElement.releasePointerCapture(e.pointerId); } catch (_) {}
    };

    const isHover = hovered === axisDef.id;
    const color = isHover ? axisDef.hover : axisDef.color;

    return (
        <group position={target}>
            {/* Видимый круг */}
            <line geometry={lineGeom}>
                <lineBasicMaterial
                    color={color}
                    linewidth={isHover ? 3 : 2}
                    transparent
                    depthTest={false}
                    opacity={isHover ? 1 : 0.85}
                />
            </line>
            {/* Невидимый тор для hit-test */}
            <mesh
                geometry={torusGeom}
                quaternion={torusQuat}
                onPointerOver={(e) => { e.stopPropagation(); setHovered(axisDef.id); }}
                onPointerOut={() => setHovered((cur) => (cur === axisDef.id ? null : cur))}
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={onUp}
            >
                <meshBasicMaterial transparent opacity={0} depthTest={false}/>
            </mesh>
        </group>
    );
};


const OrbitGizmo = () => {
    const cameraTool = useCadStore((s) => s.cameraTool);
    const { camera, controls } = useThree();
    const [hovered, setHovered] = React.useState(null);

    if (cameraTool !== 'orbit') return null;
    if (!controls) return null;

    // target и радиус — динамические: берём от текущего OrbitControls.target,
    // радиус ~ 30% дистанции до target (всегда виден независимо от зума).
    const target = [controls.target.x, controls.target.y, controls.target.z];
    const dist = camera.position.distanceTo(controls.target);
    const radius = Math.max(20, dist * 0.3);

    const onDragAngle = (axisVec) => (delta) => {
        const api = window.__kubikonCadCamera;
        if (!api?.orbitAroundAxis) return;
        api.orbitAroundAxis(axisVec, delta);
    };

    return (
        <group>
            {AXES.map((a) => (
                <OrbitCircle
                    key={a.id}
                    axisDef={a}
                    target={target}
                    radius={radius}
                    hovered={hovered}
                    setHovered={setHovered}
                    onDragAngle={onDragAngle(a.axis)}
                />
            ))}
        </group>
    );
};

export default OrbitGizmo;
